/**
 * Tiny i18n (RULES §UI-10). Keys are the English source strings:
 *
 *   t('Save changes')                         → "Wijzigingen opslaan"
 *   t('Hello, {name}', { name })              → placeholders in braces
 *   tp(n, '{count} vehicle', '{count} vehicles')  → plural by Intl.PluralRules
 *   msg('Vehicles')                           → marks a string in non-render code (nav items);
 *                                               translate it with t() when rendering
 *
 * `npm run i18n:check` extracts every literal passed to t/tp/msg and fails if nl/fr miss one.
 */
import { useSyncExternalStore } from 'react';

export type Locale = 'en' | 'nl' | 'fr';
export const LOCALES: { value: Locale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Français' },
];

// English strings are their own keys; the other catalogues load when chosen (only the active one).
// Each module keeps its own strings in src/modules/<key>/i18n/{nl,fr}.json, merged here — so
// modules never edit a shared catalogue.
type Catalogue = Record<string, string>;
const catalogues: Partial<Record<Locale, Catalogue>> = { en: {} }; // English is the key itself
const loaders: Record<Exclude<Locale, 'en'>, () => Promise<{ default: Catalogue }>> = {
  nl: () => import('./nl.json'),
  fr: () => import('./fr.json'),
};
const moduleLoaders: Record<Exclude<Locale, 'en'>, Record<string, () => Promise<{ default: Catalogue }>>> = {
  nl: import.meta.glob<{ default: Catalogue }>('../../modules/*/i18n/nl.json'),
  fr: import.meta.glob<{ default: Catalogue }>('../../modules/*/i18n/fr.json'),
};

export async function loadLocale(locale: Locale): Promise<void> {
  if (catalogues[locale] || locale === 'en') return;
  const [core, ...mods] = await Promise.all([loaders[locale](), ...Object.values(moduleLoaders[locale]).map((load) => load())]);
  catalogues[locale] = Object.assign({}, ...mods.map((m) => m.default), core.default);
}
/** BCP 47 tags for Intl formatting (Belgian conventions for nl/fr, British for en). */
export const INTL_LOCALE: Record<Locale, string> = { en: 'en-GB', nl: 'nl-BE', fr: 'fr-BE' };

let current: Locale = 'en';
const listeners = new Set<() => void>();

export function isLocale(v: unknown): v is Locale {
  return v === 'en' || v === 'nl' || v === 'fr';
}

export function getLocale(): Locale {
  return current;
}

export function setLocale(locale: Locale): void {
  if (locale === current) return;
  if (!catalogues[locale]) {
    void loadLocale(locale).then(() => setLocale(locale));
    return;
  }
  current = locale;
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  try {
    localStorage.setItem('nova.locale', locale);
  } catch {
    /* private mode */
  }
  listeners.forEach((fn) => fn());
}

export function initialLocale(preferred?: string | null): Locale {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem('nova.locale');
  } catch {
    /* private mode */
  }
  const candidates = [preferred, stored, typeof navigator !== 'undefined' ? navigator.language?.slice(0, 2) : null];
  return candidates.find(isLocale) ?? 'en';
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const s = catalogues[current]?.[key] ?? catalogues.en?.[key] ?? key;
  return interpolate(s, vars);
}

export function tp(count: number, one: string, other: string, vars?: Record<string, string | number>): string {
  const rule = new Intl.PluralRules(INTL_LOCALE[current]).select(count);
  return t(rule === 'one' ? one : other, { count, ...vars });
}

/** Identity marker so the extractor sees strings defined outside render code. */
export function msg(s: string): string {
  return s;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Re-render on language change. The app root keys on this, so every t() call updates. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale);
}
